import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, User, ChevronDown } from "lucide-react";
import { LOGO } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/artists", label: "Artists" },
  { to: "/videos", label: "Videos" },
  { to: "/events", label: "Live & Events" },
  { to: "/shop", label: "Shop" },
  { to: "/membership", label: "Membership" },
  { to: "/artist-development", label: "Development" },
  { to: "/donate", label: "Donate" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    setUserOpen(false);
    navigate("/");
  };

  return (
    <header
      className="sticky top-0 z-40 bg-white/95 backdrop-blur-xl border-b border-gray-200"
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20 gap-4">
          <Link to="/" className="flex items-center gap-3 flex-shrink-0" data-testid="nav-link-logo">
            <img src={LOGO} alt="Next Gen Praise" className="h-11 w-11 object-contain rounded-sm" />
            <div className="hidden md:block leading-tight">
              <div className="font-display text-base leading-none text-[#1F3864] font-semibold whitespace-nowrap">
                Next Gen Praise
              </div>
              <div className="text-[9px] mt-0.5 tracking-[0.2em] text-[#C00000] uppercase font-medium whitespace-nowrap">
                Praise Beyond Limits
              </div>
            </div>
          </Link>

          <nav className="hidden xl:flex items-center gap-5 flex-1 justify-center">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                className={({ isActive }) => `link-nav ${isActive ? "active text-[#1F3864]" : "text-gray-700 hover:text-[#1F3864]"}`}
                data-testid={`nav-link-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2 flex-shrink-0">
            {user ? (
              <div className="relative hidden sm:block">
                <button
                  onClick={() => setUserOpen((v) => !v)}
                  className="flex items-center gap-2 px-3 py-2 border border-gray-300 hover:border-[#1F3864] transition-colors"
                  data-testid="nav-user-menu-button"
                >
                  <User className="w-4 h-4 text-[#1F3864]" />
                  <span className="text-sm font-medium text-[#1F3864] max-w-[120px] truncate">
                    {user.name?.split(" ")[0]}
                  </span>
                  <ChevronDown className="w-3.5 h-3.5 text-gray-500" />
                </button>
                {userOpen && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-white border border-gray-200 shadow-lg py-2">
                    <Link to="/dashboard" onClick={() => setUserOpen(false)} className="block px-4 py-2.5 text-sm hover:bg-gray-50" data-testid="nav-link-dashboard">
                      My Dashboard
                    </Link>
                    {user.is_admin && (
                      <Link to="/admin" onClick={() => setUserOpen(false)} className="block px-4 py-2.5 text-sm hover:bg-gray-50 text-[#C00000] font-medium" data-testid="nav-link-admin">
                        Admin Console
                      </Link>
                    )}
                    <div className="border-t border-gray-100 my-1" />
                    <button onClick={doLogout} className="w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50" data-testid="nav-logout-button">
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className="hidden sm:inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-[#1F3864] border border-[#1F3864] hover:bg-[#1F3864] hover:text-white transition-colors whitespace-nowrap"
                data-testid="nav-link-login"
              >
                Sign In
              </Link>
            )}
            <Link
              to="/membership"
              className="btn-primary-red !py-2.5 !px-4 !text-sm hidden 2xl:inline-flex whitespace-nowrap"
              data-testid="nav-cta-join-button"
            >
              Join Community
            </Link>

            <button
              className="xl:hidden p-2"
              onClick={() => setOpen(!open)}
              aria-label="Menu"
              data-testid="nav-mobile-toggle"
            >
              {open ? <X className="w-6 h-6 text-[#1F3864]" /> : <Menu className="w-6 h-6 text-[#1F3864]" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div className="xl:hidden border-t border-gray-200 bg-white">
          <div className="max-w-7xl mx-auto px-4 py-4 grid grid-cols-2 gap-2">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className="py-2 text-sm tracking-wide uppercase text-gray-700 hover:text-[#C00000]"
                data-testid={`mobile-nav-link-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {n.label}
              </Link>
            ))}
            {user ? (
              <>
                <Link to="/dashboard" onClick={() => setOpen(false)} className="py-2 text-sm tracking-wide uppercase text-[#1F3864] font-semibold">Dashboard</Link>
                {user.is_admin && (
                  <Link to="/admin" onClick={() => setOpen(false)} className="py-2 text-sm tracking-wide uppercase text-[#C00000] font-semibold">Admin</Link>
                )}
                <button onClick={doLogout} className="py-2 text-sm tracking-wide uppercase text-left text-gray-700">Sign Out</button>
              </>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)} className="py-2 text-sm tracking-wide uppercase text-[#1F3864] font-semibold">Sign In</Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
